/**
 * The two sign-in addresses (ORDR-63).
 *
 * This app is served at the ROOT of ordering.pirouz.ai: `vite.config.ts` sets no
 * `base`, and `ORDERING_BASE_PATH` in `shell/orderingPack.ts` is `/`. So the
 * callback carries no prefix, and `useOAuthLogin` builds the `redirect_uri` as
 * `https://ordering.pirouz.ai/auth/callback`, the value paio-auth's
 * `manifests/ordering.yaml` lists under `callback_hosts` and the Google client
 * lists as an authorised redirect URI. A wrong byte here fails at Google, not
 * in a test, which is why the tests pin the exact string.
 */

export const LOGIN_PATH = "/login";

export const CALLBACK_PATH = "/auth/callback";

export const INVITE_PREFIX = "/invite";

export const INVITE_SUFFIX = "/verify";

/**
 * Where an emailed sign-in link lands (ORDR-189): `/auth/link/<token>`.
 * paio-auth builds the link as `link_base + "/" + token`, and `link_base` is
 * this origin plus this path (`config.ts`).
 */
export const SIGNIN_LINK_PATH = "/auth/link";

/**
 * The characters paio-auth's `<str:token>` can hand back in a URL path.
 * Deliberately narrow: a token is opaque, so anything outside this set means
 * the address was not the one the invitation email carried.
 */
const TOKEN = /^[A-Za-z0-9._~%-]+$/;

export type AuthScreen = "login" | "callback" | "invite" | "link";

/** Which sign-in screen an address opens, or `null` for every other address. */
export function authScreenFor(pathname: string): AuthScreen | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (path === LOGIN_PATH) {
    return "login";
  }
  if (path === CALLBACK_PATH) {
    return "callback";
  }
  if (inviteTokenFrom(path) !== null) {
    return "invite";
  }
  if (signinLinkTokenFrom(path) !== null) {
    return "link";
  }
  return null;
}

/**
 * The token in `/invite/<token>/verify`, or `null` for any other address.
 *
 * NOT DECODED. The string goes straight back to paio-auth inside a URL, so
 * decoding here and re-encoding there is two chances to change a byte in a
 * value the server issued and will compare exactly.
 */
export function inviteTokenFrom(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (!path.startsWith(`${INVITE_PREFIX}/`) || !path.endsWith(INVITE_SUFFIX)) {
    return null;
  }
  const token = path.slice(INVITE_PREFIX.length + 1, path.length - INVITE_SUFFIX.length);
  return TOKEN.test(token) ? token : null;
}

/** The address paio-auth emails, for a token. */
export function inviteHref(token: string): string {
  return `${INVITE_PREFIX}/${token}${INVITE_SUFFIX}`;
}

/**
 * The token in `/auth/link/<token>`, or `null` for any other address,
 * including `/auth/link/` with no token. NOT DECODED, for the reason
 * `inviteTokenFrom` gives.
 */
export function signinLinkTokenFrom(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (!path.startsWith(`${SIGNIN_LINK_PATH}/`)) {
    return null;
  }
  const token = path.slice(SIGNIN_LINK_PATH.length + 1);
  return TOKEN.test(token) ? token : null;
}
